import type { TickerItemOut } from "@hantavirus/shared";
import { CommunicationPatternsTable } from "#/components/dashboard/CommunicationPatternsTable.js";
import { ContradictionsTimeline } from "#/components/dashboard/ContradictionsTimeline.js";
import { CountryStatusMap } from "#/components/dashboard/CountryStatusMap.js";
import { DashboardGrid, type SlotConfig } from "#/components/dashboard/DashboardGrid.js";
import { ExposureWindowTracker } from "#/components/dashboard/ExposureWindowTracker.js";
import { FAQAccordion } from "#/components/dashboard/FAQAccordion.js";
import { Hero } from "#/components/dashboard/Hero.js";
import { InfluencerSpotlight } from "#/components/dashboard/InfluencerSpotlight.js";
import { KpiGrid } from "#/components/dashboard/KpiGrid.js";
import { NewsFeed } from "#/components/dashboard/NewsFeed.js";
import { Newswire } from "#/components/dashboard/Newswire.js";
import { OfficialSourcesBlock } from "#/components/dashboard/OfficialSourcesBlock.js";
import OutbreakGrid from "#/components/dashboard/OutbreakGrid.js";
import type { OutbreakMapRegion, OutbreakMapShip } from "#/components/dashboard/OutbreakMap.js";
import { OutbreakMap } from "#/components/dashboard/OutbreakMap.js";
import { PassengerSilenceCounter } from "#/components/dashboard/PassengerSilenceCounter.js";
import { PathogenReferenceCard } from "#/components/dashboard/PathogenReferenceCard.js";
import { PortEventsMap } from "#/components/dashboard/PortEventsMap.js";
import { TickerStrip } from "#/components/dashboard/TickerStrip.js";
import { Timeline } from "#/components/dashboard/Timeline.js";
import { UnansweredQuestions } from "#/components/dashboard/UnansweredQuestions.js";
import { VesselLiveTrack } from "#/components/dashboard/VesselLiveTrack.js";
import { createT } from "#/lib/i18n.js";
import type { TemplateLayoutProps, TemplateSlotMap } from "#/lib/templates/types.js";
import type { RegionList, ShipList } from "#/server/data-fetchers.js";

export function toMapRegions(regions: RegionList): OutbreakMapRegion[] {
  return regions.flatMap((r) => {
    if (!r.coordinates) return [];
    return [
      {
        id: r.id,
        name: r.name,
        dataText: r.dataText,
        caseCount: r.casesConfirmed ?? 0,
        coordinates: r.coordinates.coordinates as [number, number],
        risk: null,
      },
    ];
  });
}

export function toMapShip(ships: ShipList): OutbreakMapShip | null {
  const vessel = ships.find((s) => s.trackingEnabled) ?? ships[0];
  if (!vessel) return null;
  return {
    mmsi: vessel.mmsi,
    name: vessel.name,
    trackCoordinates: [],
  };
}

export function createTemplateSlots(props: TemplateLayoutProps): TemplateSlotMap {
  const {
    outbreak,
    outbreaks,
    caseCounts,
    ticker,
    news,
    newsFeedMode,
    timeline,
    faqs,
    regions,
    ships,
    pathogen,
    outbreakSources,
    locale,
  } = props;
  const t = createT(locale, "landing");
  const mapRegions = toMapRegions(regions);
  const mapShip = toMapShip(ships);

  return {
    kpi: (
      <KpiGrid
        outbreak={outbreak}
        outbreaks={outbreaks}
        caseCounts={caseCounts}
        locale={locale}
        t={t}
      />
    ),
    ticker: (
      <TickerStrip initialItems={ticker as unknown as TickerItemOut[]} locale={locale} t={t} />
    ),
    "exposure-window": <ExposureWindowTracker slug={outbreak.slug} locale={locale} t={t} />,
    pathogen: (
      <PathogenReferenceCard pathogen={pathogen} locale={locale} t={createT(locale, "pathogen")} />
    ),
    "vessel-track": mapShip ? (
      <VesselLiveTrack mmsi={mapShip.mmsi} locale={locale} t={createT(locale, "vesselTrack")} />
    ) : null,
    map: <OutbreakMap regions={mapRegions} ship={mapShip} locale={locale} t={t} />,
    "world-status": (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CountryStatusMap locale={locale} t={createT(locale, "worldMap")} />
        </div>
        <div className="lg:max-h-[460px]">
          <Newswire locale={locale} t={createT(locale, "worldMap")} />
        </div>
      </div>
    ),
    "port-events": mapShip ? (
      <PortEventsMap vesselMmsi={mapShip.mmsi} t={createT(locale, "portEvents")} />
    ) : null,
    regions: <OutbreakGrid slug={outbreak.slug} locale={locale} t={t} initialItems={regions} />,
    timeline: <Timeline slug={outbreak.slug} locale={locale} t={t} initialItems={timeline} />,
    "passenger-silence": (
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PassengerSilenceCounter
            slug={outbreak.slug}
            locale={locale}
            t={createT(locale, "passengerSilence")}
          />
        </div>
        <InfluencerSpotlight
          slug={outbreak.slug}
          locale={locale}
          t={createT(locale, "passengerSilence")}
        />
      </div>
    ),
    "communication-patterns": <CommunicationPatternsTable locale={locale} t={t} />,
    contradictions: (
      <ContradictionsTimeline locale={locale} t={createT(locale, "contradictions")} />
    ),
    "unanswered-questions": (
      <UnansweredQuestions locale={locale} t={createT(locale, "contradictions")} />
    ),
    news: (
      <NewsFeed
        slug={outbreak.slug}
        locale={locale}
        t={t}
        initialItems={news}
        feedMode={newsFeedMode}
      />
    ),
    faq: <FAQAccordion slug={outbreak.slug} locale={locale} t={t} initialItems={faqs} />,
    "official-sources": (
      <OfficialSourcesBlock
        sources={outbreakSources ?? []}
        locale={locale}
        t={createT(locale, "pathogen")}
      />
    ),
  };
}

export function BentoSlotGrid({
  props,
  layout,
  className,
}: {
  props: TemplateLayoutProps;
  layout: SlotConfig[];
  className?: string;
}) {
  return (
    <DashboardGrid
      slots={createTemplateSlots(props)}
      layout={layout}
      className={className}
      t={createT(props.locale, "dashboard")}
    />
  );
}

export {
  CommunicationPatternsTable,
  ContradictionsTimeline,
  CountryStatusMap,
  ExposureWindowTracker,
  FAQAccordion,
  Hero,
  InfluencerSpotlight,
  KpiGrid,
  NewsFeed,
  Newswire,
  OfficialSourcesBlock,
  OutbreakGrid,
  OutbreakMap,
  PassengerSilenceCounter,
  PathogenReferenceCard,
  PortEventsMap,
  TickerStrip,
  Timeline,
  UnansweredQuestions,
  VesselLiveTrack,
};
